/**
 * OutphasedRecommendationModal.jsx
 *
 * Presentational modal for outphased products on the PDP.
 * Shows warning, outphased reason, replacement product card, and contact link.
 *
 * The parent (ProductDetailPage) owns "show only once per product" logic via
 * session storage. This component simply renders when isOpen is true.
 *
 * Props:
 *   isOpen              - boolean
 *   onClose             - function
 *   product             - the outphased product object
 *   replacementProduct  - replacement product object (can be null)
 *   onReplacementClick  - function(itemCode)
 */

import { useEffect, useRef } from 'react';

export default function OutphasedRecommendationModal({
  isOpen,
  onClose,
  product,
  replacementProduct = null,
  onReplacementClick,
}) {
  const modalRef = useRef(null);

  // Escape key closes modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus the modal container on open
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen || !product) return null;

  // Resolve reason — TH uses outphasedReason, DTH uses supersessionReason
  const reason =
    (product.outphasedReason && String(product.outphasedReason).trim()) ||
    (product.supersessionReason && String(product.supersessionReason).trim()) ||
    null;

  // Backdrop click — only when clicking the backdrop itself
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Replacement product image helper
  const replacementImage =
    replacementProduct?.images?.[0]?.url ||
    replacementProduct?.imageUrl ||
    replacementProduct?.thumbnailUrl ||
    null;

  return (
    <div
      className="fixed inset-0 z-50 bg-carbon-black/50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="outphased-modal-title"
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className="bg-white shadow-xl max-w-lg w-full mx-4 p-6 relative outline-none"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-n5 hover:text-carbon-black cursor-pointer text-xl font-normal"
          aria-label="Close modal"
        >
          &times;
        </button>

        {/* Warning icon */}
        <svg
          className="w-10 h-10 text-sandvik-orange mb-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>

        {/* Heading */}
        <h2
          id="outphased-modal-title"
          className="font-sandvik-headline font-normal text-xl text-carbon-black mb-2"
        >
          This product has been outphased
        </h2>

        {/* Reason text */}
        {reason && (
          <p className="text-n7 font-sandvik-text mb-4">{reason}</p>
        )}

        {/* Replacement product card */}
        {replacementProduct ? (
          <div className="border border-n2 p-4 bg-n1 mb-4">
            <p className="text-sm text-n6 font-sandvik-text mb-1">
              Recommended replacement:
            </p>
            <div className="flex items-center gap-4">
              {replacementImage && (
                <img
                  src={replacementImage}
                  alt={replacementProduct.description || 'Replacement product'}
                  className="w-[60px] h-[60px] object-contain"
                />
              )}
              <div>
                <p className="text-carbon-black font-sandvik-text">
                  {replacementProduct.description}
                </p>
                <p className="text-sm text-n6 font-sandvik-text">
                  Part: {replacementProduct.itemCode}
                </p>
              </div>
            </div>
            <button
              onClick={() => onReplacementClick(replacementProduct.itemCode)}
              className="mt-3 text-electric-blue font-sandvik-text font-medium hover:underline text-sm"
            >
              View replacement &rsaquo;
            </button>
          </div>
        ) : (
          <p className="text-n5 font-sandvik-text italic mb-4">
            No direct replacement available.
          </p>
        )}

        {/* Contact link */}
        <a
          href="#contact"
          className="text-electric-blue font-sandvik-text hover:underline text-sm"
        >
          Contact us for alternatives &rsaquo;
        </a>
      </div>
    </div>
  );
}
